import type { CatalogueItem, FormuleBullet } from '@croissants/contracts';
import { formuleUnitPriceMinor, listIn, textIn } from '@croissants/contracts/projections';
import type { Locale } from '@croissants/i18n';
import { Button, Modal, PhotoCarousel } from '@croissants/ui';
import { type JSX, useId, useRef, useState } from 'react';
import { money } from '../lib/format';
import { type BasketChoice, MAX_LINE_QUANTITY } from '../state/basket';
import { useLocale } from '../state/locale';
import { MinusIcon, PlusIcon } from './icons';

/**
 * One product, in full (PRD GST-003).
 *
 * The tile is deliberately terse - a fixed box with two lines of description -
 * so a shelf of them scans as a shelf. Everything the tile had to cut lives
 * here: the photograph at a size worth looking at, the whole description, the
 * ingredients, and a quantity to choose before adding.
 *
 * The sheet is narrow (`sm`) on purpose. The photograph is square, like every
 * product photo on the platform, and a square is as tall as the sheet is wide -
 * so at the 800px `lg` width this used to have, the photo alone was 752px and
 * pushed the price, the allergens and the Add button off the bottom of a laptop
 * screen. A phone-width column shows a plain product and everything said about
 * it without scrolling, on any screen. A set menu's card is longer than a
 * phone, so its body scrolls inside the sheet while the quantity and Add footer
 * stay pinned and nothing scrolls the page (founder, 2026-10-03; GST-003).
 *
 * Allergens are stated HERE and nowhere else on the storefront (GST-003, PRD
 * 5.7). They are the reason this view is not optional: a guest cannot add an
 * item to the basket without passing the screen that names, in their own
 * language and in words rather than an icon, everything in it. The list is
 * never truncated here - that was the whole problem with carrying it on a
 * fixed-size tile.
 */
export function ItemDetail({
  item,
  quantity,
  onSetQuantity,
  onClose,
}: {
  item: CatalogueItem;
  /** What is already in the basket for this item; 0 when it is not in it. */
  quantity: number;
  /**
   * The guest's answers travel with the quantity (CAT-018). Absent on a plain
   * product; on a formule, one answer per bullet that offered a choice.
   */
  onSetQuantity: (quantity: number, choices?: readonly BasketChoice[]) => void;
  onClose: () => void;
}): JSX.Element {
  const { locale, t, pt } = useLocale();
  /*
   * The words the platform authored, in THIS reader's language (NFR-006). Every
   * shipped language travels with the item, so the switch moves this sheet in
   * the same render as the copy around it rather than a refetch later.
   */
  const name = textIn(locale, item.nameByLocale, item.name);
  const description = textIn(locale, item.descriptionByLocale, item.description);
  const ingredients = listIn(locale, item.ingredientsByLocale, item.ingredients);
  const soldOut = item.availability !== 'IN_STOCK';
  /*
   * "Allergens:" - and "Allergenes :" in French, which spaces its high
   * punctuation. The space is U+202F, narrow and NO-BREAK, so the colon can
   * never be left alone at the start of a wrapped line. Written as an escape
   * because it is invisible in a source file and a plain space would be wrong
   * twice over: too wide, and breakable.
   */
  const colon = locale === 'fr' ? '\u202f:' : ':';
  /*
   * Which photograph the strip is showing. A product can carry six (CAT-004)
   * and this sheet showed the first one and no sign that the others existed -
   * so a bakery that had photographed a pastry from three angles published one
   * of them. The dots are the sign, and the swipe is how a thumb reaches them.
   */
  const [photo, setPhoto] = useState(0);
  const ceiling =
    item.maxQuantityPerOrder === null
      ? MAX_LINE_QUANTITY
      : Math.min(item.maxQuantityPerOrder, MAX_LINE_QUANTITY);
  // Opening on an item already in the basket starts from what is in it, so the
  // button edits that line rather than silently resetting it to one.
  const [chosen, setChosen] = useState(Math.min(Math.max(quantity, 1), ceiling));

  /*
   * A FORMULE'S CARD, SPLIT BY ITS SECTIONS' OWN KIND (CAT-018, founder
   * decision 2026-08-30).
   *
   * An `INCLUDED` section is CONTENTS - every item on it goes in the bag,
   * each with its own count, and the guest is asked nothing. A `CHOICE`
   * section is a QUESTION and the guest picks ONE of its options; the sheet
   * asks every one of them before anything can be added, and nothing is
   * pre-selected (founder, 2026-08-28), so a guest who taps Add without
   * reading cannot be handed the first juice on the list.
   *
   * ONLY THE QUESTIONS ARE ANSWERED. The card used to echo its contents back
   * as answers too, because the number of options was the only thing saying
   * whether a section was contents or a question - and the shelf and the
   * server could read a narrowed section two different ways, which refused a
   * fully answered line as UNANSWERED. The kind is on the wire now, so there
   * is nothing to guess and nothing to echo.
   */
  const card = item.formule ?? [];
  const included = card.filter((bullet) => bullet.kind === 'INCLUDED');
  const questions = card.filter((bullet) => bullet.kind === 'CHOICE');
  const [answers, setAnswers] = useState<Readonly<Record<number, string>>>({});
  const unanswered = questions.filter((bullet) => answers[bullet.rank] === undefined);
  const choices: readonly BasketChoice[] = [...questions]
    .sort((a, b) => a.rank - b.rank)
    .flatMap((bullet) => {
      const picked = bullet.choices.find((choice) => choice.itemId === answers[bullet.rank]);
      return picked === undefined
        ? []
        : [
            {
              componentRank: bullet.rank,
              itemId: picked.itemId,
              // The words the guest read, kept the way the line's own name is:
              // the recap has to name this part on a morning the shelf cannot.
              name: textIn(locale, picked.nameByLocale, picked.name),
            },
          ];
    });
  /*
   * The button is never disabled (founder, 2026-08-29): a guest who taps Add
   * before answering is TOLD which question is open, rather than left pressing
   * a dead button. The first tap arms the sheet; from then on every unanswered
   * question wears its "Required" in red and the sheet scrolls to the first of
   * them. A question answered afterwards drops back to black on its own, and
   * the next tap either scrolls to the next open one or commits - so a card
   * with three questions walks the guest through all three.
   */
  const [armed, setArmed] = useState(false);
  const questionRefs = useRef(new Map<number, HTMLFieldSetElement>());
  const commit = () => {
    const first = unanswered[0];
    if (first !== undefined) {
      setArmed(true);
      const target = questionRefs.current.get(first.rank);
      // jsdom has no layout and no scrollIntoView; a browser always does.
      if (typeof target?.scrollIntoView === 'function') {
        target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      return;
    }
    onSetQuantity(chosen, choices.length > 0 ? choices : undefined);
    onClose();
  };

  /*
   * The quantity and the commitment are the modal's FOOTER, not the last thing
   * in its body.
   *
   * `.cib-modal__body` is the scroll container and the footer sits outside it,
   * so putting them here is what makes "Add" reachable without scrolling on any
   * screen - it was the bottom of a scrolling column before, and on a short
   * window a guest had to find and scroll a sheet most people do not think to
   * scroll. The footer's own padding is the margin around and below it.
   */
  const bar = soldOut ? undefined : (
    <div className="gw-detail__bar">
      <div className="gw-detail__stepper">
        <button
          type="button"
          className="gw-detail__step"
          disabled={chosen <= 1}
          aria-label={t('basket.decrease', { name })}
          onClick={() => setChosen((value) => Math.max(1, value - 1))}
        >
          <MinusIcon size={20} />
        </button>
        <output className="gw-detail__count" aria-label={t('basket.quantity', { name })}>
          {chosen}
        </output>
        <button
          type="button"
          className="gw-detail__step"
          disabled={chosen >= ceiling}
          aria-label={t('basket.increase', { name })}
          onClick={() => setChosen((value) => Math.min(ceiling, value + 1))}
        >
          <PlusIcon size={20} />
        </button>
      </div>

      {/* The amount is on the button because that is the commitment being made,
          exactly as the total is on the pay button at checkout - and on a set
          it FOLLOWS the picks: an option carrying a supplement moves this
          figure the moment it is taken, so the button and the checkout agree
          (CAT-018). `formuleUnitPriceMinor` is the one arithmetic; the price
          under the name stays the card's own. */}
      <div className="gw-detail__action">
        <Button onClick={commit}>
          {t('menu.addQuantity', {
            count: chosen,
            amount: money(
              formuleUnitPriceMinor(item.price, card, choices) * chosen,
              item.currency,
              locale,
            ),
          })}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={name}
      titleHidden
      size="sm"
      actions={bar}
      // The sheet may take 94% of the screen, like the payment sheet and for
      // the same reason: it is built to be read WITHOUT scrolling, and on a
      // 360x740 phone the default 88% cap left the last line of the
      // description six pixels under the footer (`.gw-detailmodal`).
      className="gw-detailmodal"
      // The cross floats over the photograph: every product sheet has one, so
      // a guest who opened a croissant by mistake has a way out other than
      // finding the backdrop (founder, 2026-08-29).
      closeLabel={pt('action.close')}
    >
      {/*
        A plain product's sheet - no set-menu card on it - lets its photograph
        give up height so the rest fits (`.gw-detail--plain`, styles.css). A set
        menu's sheet keeps its photograph and SCROLLS this body under the pinned
        footer (founder, 2026-10-03; GST-003): the sheet never outgrows the
        screen and Add never leaves it.
      */}
      <div className={card.length === 0 ? 'gw-detail gw-detail--plain' : 'gw-detail'}>
        {item.images.length === 0 ? null : (
          <div className="gw-detail__photo">
            <PhotoCarousel
              className="gw-detail__image"
              images={item.images}
              index={photo}
              onIndex={setPhoto}
              dotLabel={(position, count) => t('menu.photoPosition', { position, count })}
            />
          </div>
        )}

        {/* Everything under the photograph, in one block: in a plain sheet the
            photograph and this are the two flex items, so the margins in here
            keep collapsing exactly as they did. */}
        <div className="gw-detail__text">
          <h2 className="gw-detail__name">{name}</h2>
          <p className="gw-detail__price">{money(item.price, item.currency, locale)}</p>

          {description === '' ? null : <p className="gw-detail__description">{description}</p>}

          {ingredients.length > 0 ? (
            <p className="gw-detail__meta">
              <span className="gw-detail__metaLabel">
                {pt('storefront.ingredients')}
                {colon}
              </span>{' '}
              {ingredients.join(', ')}
            </p>
          ) : null}

          {item.maxQuantityPerOrder === null ? null : (
            <p className="gw-detail__meta">
              {t('menu.maxPerOrder', { count: item.maxQuantityPerOrder })}
            </p>
          )}

          {included.length > 0 ? (
            <FormuleContents
              bullets={included}
              label={t('menu.formule.included')}
              locale={locale}
            />
          ) : null}

          {questions.map((bullet) => (
            <FormuleQuestion
              key={bullet.rank}
              bullet={bullet}
              locale={locale}
              currency={item.currency}
              picked={answers[bullet.rank]}
              required={t('menu.formule.required')}
              missing={armed && answers[bullet.rank] === undefined}
              onPick={(itemId) => setAnswers((current) => ({ ...current, [bullet.rank]: itemId }))}
              onRef={(element) => {
                if (element === null) questionRefs.current.delete(bullet.rank);
                else questionRefs.current.set(bullet.rank, element);
              }}
            />
          ))}

          {soldOut ? <p className="gw-detail__soldout">{pt('storefront.soldOutToday')}</p> : null}

          {/* LAST, and quieter (founder, 2026-08-29): the safety line still lives on
            this sheet and nowhere else (GST-003), but under the card rather than
            above it, so the choices a guest has to make are not pushed down by
            a line they read once. The cross-contamination line is on EVERY
            sheet: the declaration describes the recipe, not the kitchen, and a
            product declaring nothing is the one most easily read as safe. */}
          <div className="gw-detail__foot">
            {item.allergens.length > 0 ? (
              <p className="gw-detail__footLine">
                {pt('storefront.allergens')}
                {colon} {item.allergens.map((code) => t(`menu.allergen.${code}`)).join(', ')}
              </p>
            ) : null}
            {item.dietaryTags.length > 0 ? (
              <p className="gw-detail__footLine">
                {t('menu.dietary')}
                {colon} {item.dietaryTags.map((tag) => t(`menu.dietary.${tag}`)).join(', ')}
              </p>
            ) : null}
            <p className="gw-detail__footLine">{t('menu.crossContamination')}</p>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/**
 * What goes in the bag whatever the guest picks: ONE sentence, "1x Croissant,
 * 1x Pain au chocolat, 1x Half baguette" (founder, 2026-08-29) - a count on
 * every item, even one, and commas between them, because there is nothing to
 * decide and a list of rows made it look as though there were.
 *
 * EVERY `INCLUDED` section pours into this one sentence, in card order, and
 * each item carries its OWN count (founder, 2026-08-30): a section holds
 * several items now, so the count is the item's rather than the section's.
 */
function FormuleContents({
  bullets,
  label,
  locale,
}: {
  bullets: readonly FormuleBullet[];
  label: string;
  /** The reader's language, so the contents follow the switch (NFR-006). */
  locale: string;
}): JSX.Element {
  return (
    <section className="gw-formule">
      <h3 className="gw-formule__heading">{label}</h3>
      <p className="gw-formule__included">{includedSentence(bullets, locale)}</p>
    </section>
  );
}

/** "1x Croissant, 2x Pain au chocolat" - exported for the test, pure. */
export function includedSentence(bullets: readonly FormuleBullet[], locale: string): string {
  return bullets
    .flatMap((bullet) =>
      bullet.choices.map(
        (choice) => `${choice.quantity}x ${textIn(locale, choice.nameByLocale, choice.name)}`,
      ),
    )
    .join(', ');
}

/**
 * One question of the card: a radio group, one option per product, the name
 * on the left and the circle on the right edge (founder, 2026-08-29). No option
 * is checked until the guest checks one.
 *
 * An option that costs more says so, between the name and the circle: the
 * supplement alone, with a plus in front of it and no words, at the END edge
 * where every figure on this platform sits. Options at the section's floor say
 * nothing at all - "+ 0,00" beside four juices is noise, and the price the set
 * already carries is the price of taking any of them.
 */
function FormuleQuestion({
  bullet,
  locale,
  currency,
  picked,
  required,
  missing,
  onPick,
  onRef,
}: {
  bullet: FormuleBullet;
  /** The reader's language, so the question and its options follow the switch. */
  locale: Locale;
  /** The set's own currency - a supplement is money in the same one. */
  currency: string;
  picked: string | undefined;
  /** "Required", already translated, on the right of the title. */
  required: string;
  /** True once the guest tried to add without answering THIS question. */
  missing: boolean;
  onPick: (itemId: string) => void;
  onRef: (element: HTMLFieldSetElement | null) => void;
}): JSX.Element {
  const group = useId();
  return (
    <fieldset
      ref={onRef}
      className={`gw-formule gw-formule--question${missing ? ' gw-formule--missing' : ''}`}
      aria-invalid={missing || undefined}
    >
      {/* A legend cannot lay out as a row, so the title is a block inside it:
          the question on the left, "Required" flush right, red while open. */}
      <legend className="gw-formule__legend">
        <span className="gw-formule__heading gw-formule__row">
          <span>{textIn(locale, bullet.noteByLocale, bullet.note)}</span>
          <span className="gw-formule__required">{required}</span>
        </span>
      </legend>
      {bullet.choices.map((choice) => (
        <label key={choice.itemId} className="gw-formule__option">
          <span className="gw-formule__optionName">
            {textIn(locale, choice.nameByLocale, choice.name)}
          </span>
          {choice.supplement > 0 ? (
            <span className="gw-formule__optionExtra">
              + {money(choice.supplement, currency, locale)}
            </span>
          ) : null}
          <input
            type="radio"
            name={group}
            value={choice.itemId}
            checked={picked === choice.itemId}
            onChange={() => onPick(choice.itemId)}
          />
        </label>
      ))}
    </fieldset>
  );
}
